import React from 'react';

import { useTheme } from 'styled-components';
import { ThemeType } from 'theme/theme';

import { ReactComponent as ExpandMoreIcon } from 'assets/images/expand-more.svg';
import { ReactComponent as BackpackActive } from 'assets/images/luggages/backpack-active.svg';
import { ReactComponent as HandLuggageActive } from 'assets/images/luggages/hand-luggage-active.svg';
import { ReactComponent as HandLuggage } from 'assets/images/luggages/hand-luggage.svg';
import { ReactComponent as WarehouseLuggageActive } from 'assets/images/luggages/warehouse-luggage-active.svg';
import { ReactComponent as WarehouseLuggage } from 'assets/images/luggages/warehouse-luggage.svg';
import Flightline from 'components/FlightLine';
import useBoolean from 'hooks/useBoolean';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { GroupedSectorOptions } from 'models/flightSearch';

import { formatDuration, getTime, getTurnDays } from '../../utils';
import CollapsibleDetail from '../CollapsibleDetail';
import { FlightTimeText } from '../FlightOptionDesktop/styles';
import {
  AirlineLogo,
  AirlineName,
  AirlineRadioWrapper,
  ExpandMoreButton,
  FlightAirportNameText,
  FlightContainer,
  FlightDetailContainer,
  LeftColumn,
  FlightLuggages,
  FlightLuggagesContainer,
  FlightTimeArrivalWrapper,
  FlightTimeContainer,
  FormControlLabel,
  RadioButton,
  TurnDays,
  RightColumn,
} from './styles';

interface Props {
  groupedSectorOption: GroupedSectorOptions;
}

const FlightOptionMobile = ({ groupedSectorOption }: Props) => {
  const { t } = useStoreTranslation();
  const showDetail = useBoolean(false);
  const { flights, totalDuration, flightKeys } = groupedSectorOption;
  const numberOfStops = flights.length - 1;
  const departureFlight = flights.first();
  const arrivalFlight = flights.last();
  const turnDays = getTurnDays(departureFlight.departureDate, arrivalFlight.arrivalDate);
  const theme = useTheme() as ThemeType;
  return (
    <>
      <FlightContainer>
        <LeftColumn>
          <AirlineRadioWrapper>
            <FormControlLabel
              value={flightKeys}
              control={<RadioButton disableRipple color="default" />}
              label={undefined}
            />
            <AirlineLogo airline={departureFlight.airlineCode} />
          </AirlineRadioWrapper>
          <FlightDetailContainer>
            <FlightTimeContainer>
              <FlightTimeText>{getTime(departureFlight.departureDate)}</FlightTimeText>
              <FlightAirportNameText>{departureFlight.departureAirport}</FlightAirportNameText>
            </FlightTimeContainer>
            <Flightline
              points={numberOfStops}
              topText={formatDuration(totalDuration)}
              bottomText={
                numberOfStops === 0 ? t('flightsCard.direct') : t('flightsCard.stopNumber', { count: numberOfStops })
              }
            />
            <FlightTimeContainer>
              <FlightTimeArrivalWrapper>
                {turnDays > 0 && <TurnDays>{`+${turnDays}`}</TurnDays>}
                <FlightTimeText>{getTime(arrivalFlight.arrivalDate)}</FlightTimeText>
              </FlightTimeArrivalWrapper>
              <FlightAirportNameText>{arrivalFlight.arrivalAirport}</FlightAirportNameText>
            </FlightTimeContainer>
          </FlightDetailContainer>
        </LeftColumn>
        <RightColumn>
          <FlightLuggagesContainer>
            <AirlineName>{departureFlight.airlineName}</AirlineName>
            <FlightLuggages>
              <BackpackActive fill={theme.primaryBackgroundColor} stroke={theme.primaryBackgroundColor} />
              {departureFlight.handBaggage ? (
                <HandLuggageActive fill={theme.primaryBackgroundColor} stroke={theme.primaryBackgroundColor} />
              ) : (
                <HandLuggage />
              )}
              {departureFlight.baggageRestrictions && departureFlight.baggageRestrictions.startsWith('0') ? (
                <WarehouseLuggage />
              ) : (
                <WarehouseLuggageActive fill={theme.primaryBackgroundColor} stroke={theme.primaryBackgroundColor} />
              )}
            </FlightLuggages>
          </FlightLuggagesContainer>
          <ExpandMoreButton rotated={showDetail.value} onClick={showDetail.toggle}>
            <ExpandMoreIcon />
          </ExpandMoreButton>
        </RightColumn>
      </FlightContainer>
      <CollapsibleDetail expanded={showDetail.value} flights={flights} totalDuration={totalDuration} />
    </>
  );
};

export default FlightOptionMobile;
